import Checkbox from '@mui/material/Checkbox';
import type { ChangeEvent } from 'react';
import { memo, useCallback } from 'react';

import {
  StyledBodyCell,
  StyledCheckboxCell,
  StyledTableRow,
} from './table-styles';

import type { Column, TableData, TableRowProps } from 'src/types/table';

export const TableRow = memo(
  ({
    row,
    columns,
    showCheckbox,
    selectedRows = [],
    onSelectRow,
    onRowClick,
    isRowSelectable,
  }: TableRowProps): JSX.Element => {
    const isSelected = selectedRows.includes(String(row.id));
    const canSelect = isRowSelectable ? isRowSelectable(row) : true;

    const handleRowClick = useCallback(
      (event: React.MouseEvent): void => {
        if (onRowClick) {
          onRowClick(row, event);
        }
      },
      [onRowClick, row],
    );

    const handleCheckboxClick = useCallback(
      (event: ChangeEvent<HTMLInputElement>): void => {
        event.stopPropagation();
        if (!canSelect) return;
        onSelectRow?.(String(row.id));
      },
      [canSelect, onSelectRow, row.id],
    );

    const renderCell = useCallback(
      (column: Column, value: TableData[keyof TableData]): React.ReactNode => {
        if (column.render) {
          return column.render(row);
        }

        let displayValue = String(value);

        if (value === undefined) {
          displayValue = 'N/A';
        }
        return displayValue;
      },
      [row],
    );

    return (
      <StyledTableRow
        onClick={handleRowClick}
        $clickable={onRowClick ? true : undefined}>
        {showCheckbox && (
          <StyledCheckboxCell>
            <Checkbox
              checked={isSelected}
              disabled={!canSelect}
              onChange={handleCheckboxClick}
              onClick={(e) => e.stopPropagation()}
            />
          </StyledCheckboxCell>
        )}
        {columns.map((column) => (
          <StyledBodyCell
            key={column.id}
            $cellwidth={
              column.width !== undefined ? String(column.width) : undefined
            }>
            {renderCell(column, row[column.id])}
          </StyledBodyCell>
        ))}
      </StyledTableRow>
    );
  },
);
